"use client"

import * as React from "react"
import Link from "next/link"
import { Check, PackageCheck, ShoppingBag } from "lucide-react"

import { Button } from "@/components/ui/button"
import type { ColourwayId, Product } from "@/features/catalog/catalog"
import { ProductGallery } from "@/features/catalog/components/product-gallery"
import { SwatchPicker } from "@/features/catalog/components/swatch-picker"
import { comboShortfall, startingColours, type StorefrontCombo } from "@/features/combos/combo"
import { ComboTrio } from "@/features/combos/components/combo-trio"
import { useAddCombo, useComboPicks } from "@/features/combos/hooks/use-combo-picks"
import { discountPercent, formatMoney } from "@/lib/money"

/**
 * The combo page's top: the pack as picked and the mount's photographs, a colour for each mount,
 * and the price.
 */
export function ComboBuilder({
  combo,
  gallery,
}: {
  combo: StorefrontCombo
  /** The mount's photographs in each finish (catalog.ts). */
  gallery: Product["gallery"]
}) {
  const { colourways } = combo.product
  const [colours, setColours] = React.useState<ColourwayId[]>(() =>
    startingColours(colourways, combo.quantity),
  )
  const [justAdded, setJustAdded] = React.useState(false)
  const addToCart = useAddCombo(combo)
  // Shared with the phone's bar, so its Add to cart adds this pick.
  const setPicks = useComboPicks((s) => s.set)
  React.useEffect(() => setPicks(combo.slug, colours), [combo.slug, colours, setPicks])
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  React.useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const picked = colours.map((id) => colourways.find((c) => c.id === id)!)
  const [shot, setShot] = React.useState(0)
  // Each shot in one of the picked colours, in turn, so the photos show the pack as picked.
  const shots = gallery[colourways[0]!.id].map((_, i) => {
    const colour = picked[i % picked.length]!
    const image = gallery[colour.id][i]!
    return { ...image, alt: `${colour.name} — ${image.alt}` }
  })
  const off = discountPercent(combo.compareAt, combo.price)
  const short = comboShortfall(colourways, colours)

  const pick = (slot: number, id: ColourwayId) =>
    setColours((list) => list.map((c, i) => (i === slot ? id : c)))

  const add = (e: React.MouseEvent<HTMLButtonElement>) => {
    addToCart(colours, e.currentTarget)
    setJustAdded(true)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setJustAdded(false), 1800)
  }

  return (
    <div
      id="buy"
      className="grid grid-cols-1 gap-8 px-5 py-8 sm:px-8 lg:grid-cols-[minmax(0,calc(100dvh-19rem))_minmax(0,1fr)] lg:gap-14 lg:py-10 xl:px-14"
    >
      <ProductGallery
        images={shots}
        shot={shot}
        onShot={setShot}
        lead={{
          thumb: combo.image,
          alt: `${combo.name} in the colours picked`,
          view: (
            <div className="bg-void absolute inset-0 flex items-center justify-center px-[5%]">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_65%_at_50%_62%,rgb(255_90_31_/_0.2),transparent_70%)]" />
              <ComboTrio
                colourways={picked}
                sizes="(min-width: 1024px) 18vw, 36vw"
                priority
                className="w-full"
              />
            </div>
          ),
        }}
      />

      <div className="flex flex-col">
        <span className="text-blaze mb-3 font-mono text-[11.5px] tracking-[0.2em] uppercase">
          Combo pack · {combo.quantity} mounts
        </span>
        <h1 className="font-display text-bone text-[38px] leading-[1.04] uppercase sm:text-[46px] lg:text-[40px] xl:text-[54px]">
          {combo.name}
        </h1>
        {combo.summary ? (
          <p className="text-ash mt-4 max-w-[520px] text-[16px] leading-[1.6]">{combo.summary}</p>
        ) : null}

        <div className="mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-bone font-display text-[44px] leading-none">
            {formatMoney(combo.price)}
          </span>
          {off > 0 ? (
            <>
              <span className="text-dim font-mono text-[16px] line-through">
                {formatMoney(combo.compareAt)}
              </span>
              <span className="bg-acid text-void rounded px-2 py-1 font-mono text-[12px] font-bold tracking-[0.08em]">
                SAVE {off}%
              </span>
            </>
          ) : null}
        </div>
        <p className="text-dim mt-2 text-[13px]">Inclusive of all taxes · Shipping by pincode</p>

        <fieldset className="mt-8">
          <legend className="text-ash mb-4 font-mono text-[11.5px] tracking-[0.2em] uppercase">
            Pick a colour for each mount
          </legend>
          <ol className="flex flex-col divide-y divide-white/[0.07] border-y border-white/[0.07]">
            {colours.map((id, slot) => (
              <li key={slot} className="flex items-center justify-between gap-4 py-3.5">
                <span className="text-bone text-[14.5px] font-semibold">Mount {slot + 1}</span>
                <SwatchPicker
                  colourways={colourways}
                  picked={id}
                  onPick={(next) => pick(slot, next)}
                  className="justify-end"
                />
              </li>
            ))}
          </ol>
        </fieldset>

        {short.length ? (
          <p role="alert" className="text-magenta mt-4 text-[13.5px]">
            {short.join(". ")}. Pick another colour for that mount.
          </p>
        ) : null}

        <Button
          type="button"
          variant="primary"
          size="lg"
          full
          className="mt-6"
          disabled={short.length > 0}
          onClick={add}
        >
          {justAdded ? (
            <>
              <Check className="size-4" strokeWidth={2.6} />
              Added
            </>
          ) : (
            <>
              <ShoppingBag className="size-4" strokeWidth={1.9} />
              Add the {combo.quantity} to cart · {formatMoney(combo.price)}
            </>
          )}
        </Button>

        <div className="rounded-tile mt-8 border border-white/[0.07] bg-white/[0.02] p-5">
          <h2 className="text-dim mb-3 flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase">
            <PackageCheck className="text-acid size-4" strokeWidth={2} />
            In the box
          </h2>
          <ul className="text-ash flex flex-col gap-2 text-[14px]">
            <li className="flex gap-2.5">
              <Check className="text-acid mt-0.5 size-4 shrink-0" strokeWidth={2.6} />
              {combo.quantity} × {combo.product.name}, arm attached, in the colours you pick
            </li>
            {combo.extras.map((line) => (
              <li key={line} className="flex gap-2.5">
                <Check className="text-acid mt-0.5 size-4 shrink-0" strokeWidth={2.6} />
                {line}
              </li>
            ))}
          </ul>
          <Link
            href={`/product/${combo.product.slug}`}
            className="text-blaze hover:text-ember mt-4 inline-block text-[13.5px] font-semibold"
          >
            Everything about the {combo.product.name} →
          </Link>
        </div>
      </div>
    </div>
  )
}
